import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import GraphChart from '../../features/equalizer/GraphChart';
import type { GraphAdapter } from '../../types';
import { TestResult, EqualLoudnessContour } from '../../types/index';
import { Logger } from '../../core/logger';
const logger = Logger.createComponentLogger('ReactGraphAdapter');

interface GraphData {
  results?: { [key: string]: TestResult[] };
  contours?: { [key: string]: EqualLoudnessContour[] };
  selectedEar?: 'left' | 'right' | 'both';
  selectedContour?: string;
}

export class ReactGraphAdapter implements GraphAdapter {
  mountId: string;
  options: any;
  root: Root | null;
  container: HTMLElement | null;

  constructor(mountId: string, options: any = {}) {
    this.mountId = mountId;
    this.options = options;
    this.root = null;
    this.container = null;
    this.initialize();
  }

  initialize() {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(this.mountId);
    if (!el) {
      logger.error('Mount element not found:', this.mountId);
      return;
    }

    // If the existing element is a canvas, replace it with a div so React can mount
    if (el.tagName.toLowerCase() === 'canvas') {
      const wrapper = document.createElement('div');
      wrapper.id = this.mountId; // keep same id so other code finds it
      el.parentNode?.replaceChild(wrapper, el);
      this.container = wrapper;
    } else {
      this.container = el as HTMLElement;
    }

    try {
      this.root = createRoot(this.container);
      // Initial empty render to satisfy React root
      this.root.render(React.createElement('div'));
    } catch (err) {
      logger.error('Failed to create React root for graph mount:', err);
      this.root = null;
    }
  }

  /**
   * Render method to match GraphRenderer.render signature.
   * Returns true on success, false on failure.
   */
  render(data: GraphData = {}) {
    if (!this.root || !this.container) {
      logger.error('ReactGraphAdapter not initialized');
      return false;
    }

    try {
      this.root.render(
        React.createElement(GraphChart, {
          results: data.results,
          contours: data.contours,
          selectedEar: data.selectedEar,
          selectedContour: data.selectedContour,
          options: this.options,
        })
      );
      return true;
    } catch (error) {
      logger.error('Error in ReactGraphAdapter.render():', error);
      return false;
    }
  }

  /**
   * Update adapter options (passed to the React component on next render)
   */
  updateOptions(newOptions: any) {
    Object.assign(this.options, newOptions);
  }

  /**
   * Destroy the adapter and unmount the React root to free resources.
   */
  destroy() {
    try {
      if (this.root && typeof (this.root as any).unmount === 'function') {
        (this.root as any).unmount();
      } else if (this.root && typeof (this.root as any).render === 'function') {
        // Fallback for older React roots
        (this.root as any).render(null);
      }
    } catch (err) {
      logger.error('Error during ReactGraphAdapter.destroy():', err);
    } finally {
      this.root = null;
      this.container = null;
    }
  }

  /**
   * toDataURL compatibility method - serializes rendered SVG (Recharts) to a PNG data URL.
   * Returns a Promise resolving to a data URL string, or null if export is not possible.
   */
  async toDataURL(): Promise<string | null> {
    if (typeof document === 'undefined' || !this.container) return null;

    const svg = this.container.querySelector('svg') as SVGSVGElement | null;
    if (!svg) return null;

    try {
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(svg);

      // Ensure namespace present
      if (!svgString.includes('xmlns')) {
        svgString = svgString.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';

      const dataUrl = await new Promise<string>((resolve, reject) => {
        img.onload = () => {
          try {
            const rect = svg.getBoundingClientRect();
            const width = Math.max(1, Math.round(rect.width)) || Number(this.options?.width) || 800;
            const height = Math.max(1, Math.round(rect.height)) || Number(this.options?.height) || 400;

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(url);
              reject(new Error('Failed to get canvas context'));
              return;
            }

            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);

            try {
              const png = canvas.toDataURL('image/png');
              resolve(png);
            } catch (err) {
              reject(err);
            } finally {
              URL.revokeObjectURL(url);
            }
          } catch (err) {
            URL.revokeObjectURL(url);
            reject(err);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load SVG into Image'));
        };
        img.src = url;
      });

      return dataUrl;
    } catch (err) {
      logger.error('ReactGraphAdapter.toDataURL error:', err);
      return null;
    }
  }
}

export function createReactGraphAdapter(mountId: string, options?: any) {
  return new ReactGraphAdapter(mountId, options);
}
